import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { payrollService } from '../../../services/payrollService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayrollAlerts = ({ alerts, onRefresh }) => {
  const { user } = useAuth();

  const handleResolveAlert = async (alertId) => {
    try {
      await payrollService?.resolvePayrollAlert(alertId, user?.id);
      alert('Alert resolved successfully');
      onRefresh();
    } catch (error) {
      console.error('Error resolving alert:', error);
      alert('Error resolving alert');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'missing_timesheet':
        return 'Clock';
      case 'calculation_error':
        return 'AlertTriangle';
      case 'approval_required':
        return 'CheckCircle';
      case 'compliance_issue':
        return 'Shield';
      case 'payment_failed':
        return 'XCircle';
      default:
        return 'Bell';
    }
  };

  const groupedAlerts = alerts?.reduce((acc, alert) => {
    const severity = alert?.severity || 'medium';
    if (!acc?.[severity]) acc[severity] = [];
    acc?.[severity]?.push(alert);
    return acc;
  }, {});

  const severityOrder = ['critical', 'high', 'medium', 'low'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Payroll Alerts & Notifications</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {alerts?.length} active alert{alerts?.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {alerts?.length === 0 ? (
        <div className="text-center py-12 border border-border rounded-lg">
          <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">All Clear!</h3>
          <p className="text-muted-foreground">No active payroll alerts at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {severityOrder?.map((severity) => {
            const severityAlerts = groupedAlerts?.[severity];
            if (!severityAlerts || severityAlerts?.length === 0) return null;

            return (
              <div key={severity}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase ${getSeverityColor(severity)}`}>
                    {severity}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {severityAlerts?.length} alert{severityAlerts?.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-3">
                  {severityAlerts?.map((alert) => (
                    <div
                      key={alert?.id}
                      className={`border rounded-lg p-4 ${getSeverityColor(alert?.severity)} bg-opacity-50`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <Icon name={getAlertIcon(alert?.alert_type)} size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-foreground capitalize">
                                {alert?.alert_type?.replace('_', ' ')}
                              </h4>
                              {alert?.user_profiles?.full_name && (
                                <p className="text-sm text-muted-foreground">
                                  Employee: {alert?.user_profiles?.full_name}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(alert?.created_at)?.toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-foreground mb-3">{alert?.message}</p>
                          <Button
                            onClick={() => handleResolveAlert(alert?.id)}
                            variant="outline"
                            size="sm"
                          >
                            <Icon name="Check" size={16} className="mr-1" />
                            Resolve Alert
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Alert Types Legend */}
      <div className="border border-border rounded-lg p-4 bg-muted/30">
        <h4 className="font-medium text-foreground mb-3">Alert Types</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Missing Timesheet</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Calculation Error</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Approval Required</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Compliance Issue</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="XCircle" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Payment Failed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollAlerts;